import React, { useMemo, useState, useEffect } from 'react';
import { getYear, getMonth, format } from 'date-fns';
import { formatCurrency } from '../../utils/formatCurrency';
import { CATEGORY_STYLES, CATEGORY_ORDER, MONTH_NAMES_SHORT, getCategoryStyle } from './categoryStyles';

const TransactionsListV3 = ({ transactions, onDeleteTransaction }) => {
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);

  const availableYears = useMemo(() => {
    const years = (transactions || [])
      .filter(t => t && t.date)
      .map(t => { try { return getYear(new Date(t.date)); } catch { return null; } })
      .filter(Boolean);
    const unique = [...new Set(years)].sort((a, b) => b - a);
    return unique.length ? unique : [now.getFullYear()];
  }, [transactions]);

  const availableMonths = useMemo(() => {
    const set = new Set();
    (transactions || []).forEach(t => {
      if (!t || !t.date) return;
      try {
        const d = new Date(t.date);
        if (getYear(d) === selectedYear) set.add(getMonth(d));
      } catch {}
    });
    return [...set].sort((a, b) => a - b);
  }, [transactions, selectedYear]);

  useEffect(() => {
    if (availableYears.length && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  useEffect(() => {
    if (availableMonths.length && !availableMonths.includes(selectedMonth)) {
      setSelectedMonth(availableMonths[availableMonths.length - 1]);
    }
  }, [availableMonths, selectedMonth]);

  const monthTransactions = useMemo(() => {
    return (transactions || [])
      .filter(t => t && t.date && t.merchantName)
      .filter(t => {
        try {
          const d = new Date(t.date);
          return getYear(d) === selectedYear && getMonth(d) === selectedMonth;
        } catch { return false; }
      });
  }, [transactions, selectedYear, selectedMonth]);

  const categorySummary = useMemo(() => {
    const map = {};
    monthTransactions.forEach(t => {
      const c = t.expenseType || 'OTHERS';
      if (!map[c]) map[c] = { name: c, count: 0, total: 0 };
      map[c].count += 1;
      map[c].total += (t.gbpAmount || 0);
    });
    return Object.values(map).sort((a, b) => {
      const ia = CATEGORY_ORDER.indexOf(a.name);
      const ib = CATEGORY_ORDER.indexOf(b.name);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
  }, [monthTransactions]);

  const filtered = useMemo(() => {
    let list = monthTransactions;
    if (activeCategory) list = list.filter(t => t.expenseType === activeCategory);
    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      list = list.filter(t =>
        (t.merchantName || '').toLowerCase().includes(s) ||
        (t.expenseType || '').toLowerCase().includes(s) ||
        (t.bankName || '').toLowerCase().includes(s) ||
        (t.moneySource || '').toLowerCase().includes(s)
      );
    }
    return [...list].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [monthTransactions, activeCategory, searchTerm]);

  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach(t => {
      const key = t.date.slice(0, 10);
      if (!groups[key]) groups[key] = [];
      groups[key].push(t);
    });
    return Object.entries(groups).sort(([a], [b]) => (a < b ? 1 : -1));
  }, [filtered]);

  const monthTotal = monthTransactions.reduce((s, t) => s + (t.gbpAmount || 0), 0);
  const filteredTotal = filtered.reduce((s, t) => s + (t.gbpAmount || 0), 0);
  const avg = filtered.length ? filteredTotal / filtered.length : 0;

  const handleDelete = (id, merchantName) => {
    if (!window.confirm(`¿Eliminar "${merchantName}"?`)) return;
    onDeleteTransaction(id);
  };

  return (
    <div className="v3-card-dark rounded-3xl p-6 md:p-8 shadow-2xl">
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="text-xs font-semibold tracking-widest text-slate-400 uppercase mb-1">Transacciones</p>
          <h2 className="text-2xl md:text-3xl font-bold text-white">
            {MONTH_NAMES_SHORT[selectedMonth]} {selectedYear}
          </h2>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-400 uppercase tracking-widest">Total</p>
          <p className="text-2xl md:text-3xl font-bold text-white tabular-nums">{formatCurrency(monthTotal)}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1 v3-scroll">
        {availableYears.map(y => (
          <button
            key={y}
            onClick={() => setSelectedYear(y)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition ${
              selectedYear === y ? 'bg-white text-slate-900' : 'bg-white/5 text-slate-300 hover:bg-white/10'
            }`}
          >
            {y}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1 v3-scroll">
        {availableMonths.length === 0 ? (
          <span className="text-sm text-slate-500 px-2">Sin datos para este año</span>
        ) : availableMonths.map(m => (
          <button
            key={m}
            onClick={() => setSelectedMonth(m)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition ${
              selectedMonth === m
                ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg shadow-fuchsia-500/20'
                : 'bg-white/5 text-slate-300 hover:bg-white/10'
            }`}
          >
            {MONTH_NAMES_SHORT[m]}
          </button>
        ))}
      </div>

      <div className="relative mb-6">
        <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Buscar comercio, categoría, banco..."
          className="w-full pl-11 pr-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/50 focus:border-transparent transition"
        />
      </div>

      {categorySummary.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 v3-scroll">
            <button
              onClick={() => setActiveCategory(null)}
              className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition ${
                !activeCategory ? 'bg-white text-slate-900' : 'bg-white/5 text-slate-300 hover:bg-white/10'
              }`}
            >
              Todas · {monthTransactions.length}
            </button>
            {categorySummary.map(c => {
              const s = getCategoryStyle(c.name);
              const isActive = activeCategory === c.name;
              return (
                <button
                  key={c.name}
                  onClick={() => setActiveCategory(isActive ? null : c.name)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition ${
                    isActive ? `${s.bg} text-white` : 'bg-white/5 text-slate-300 hover:bg-white/10'
                  }`}
                >
                  <span>{s.emoji}</span>
                  <span>{s.label}</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${isActive ? 'bg-black/20' : 'bg-white/10'}`}>{c.count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3 mb-6">
        <Stat label="Transacciones" value={filtered.length} />
        <Stat label="Total" value={formatCurrency(filteredTotal)} accent />
        <Stat label="Promedio" value={formatCurrency(avg)} />
      </div>

      {grouped.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <p className="text-5xl mb-3">💸</p>
          <p className="text-sm">No hay transacciones para este filtro</p>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(([date, items]) => {
            const d = new Date(date);
            const dayTotal = items.reduce((s, t) => s + (t.gbpAmount || 0), 0);
            return (
              <div key={date}>
                <div className="flex items-center justify-between mb-3 px-1">
                  <p className="text-sm font-semibold text-slate-300">
                    {format(d, 'EEEE, d MMM')}
                  </p>
                  <p className="text-sm font-semibold text-slate-400 tabular-nums">-{formatCurrency(dayTotal)}</p>
                </div>
                <div className="space-y-2">
                  {items.map(t => (
                    <TransactionRow
                      key={t.id}
                      transaction={t}
                      onDelete={() => handleDelete(t.id, t.merchantName)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, accent }) => (
  <div className={`rounded-2xl p-4 ${accent ? 'bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 border border-fuchsia-400/30' : 'bg-white/5 border border-white/10'}`}>
    <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">{label}</p>
    <p className="text-lg font-bold text-white tabular-nums">{value}</p>
  </div>
);

const TransactionRow = ({ transaction: t, onDelete }) => {
  const s = getCategoryStyle(t.expenseType);
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      onClick={() => setExpanded(!expanded)}
      className="group flex items-center gap-3 p-3 rounded-2xl bg-white/5 hover:bg-white/10 transition cursor-pointer border border-transparent hover:border-white/10"
    >
      <div className={`shrink-0 w-11 h-11 rounded-full ${s.bg} flex items-center justify-center text-lg shadow-lg`}>
        <span className="text-white">{s.emoji}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-white truncate">{t.merchantName}</p>
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <span className={s.text}>{s.label}</span>
          {t.bankName && <span>· {t.bankName}</span>}
          {t.moneySource && !t.bankName && <span>· {t.moneySource}</span>}
        </div>
      </div>
      <div className="text-right shrink-0">
        <p className="font-bold text-white tabular-nums">-{formatCurrency(t.gbpAmount || 0)}</p>
        {t.currency && t.currency !== 'GBP' && t.currency !== 'POUNDS' && t.originalAmount ? (
          <p className="text-[11px] text-slate-500 tabular-nums">{t.originalAmount.toFixed(2)} {t.currency}</p>
        ) : null}
      </div>
      {expanded && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="ml-2 shrink-0 p-2 rounded-full bg-red-500/10 hover:bg-red-500/20 text-red-400 transition"
          title="Eliminar"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default TransactionsListV3;
